import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DailyHealthScore,
  HealthScoreBreakdown,
  StreakInfo,
  Achievement,
  AchievementType,
} from '../types';
import './HealthScorePage.css';

// Mock data - will be replaced with API calls
const generateMockWeekData = (): DailyHealthScore[] => {
  const data: DailyHealthScore[] = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const breakdown: HealthScoreBreakdown = {
      activity: Math.floor(Math.random() * 40) + 60,
      sleep: Math.floor(Math.random() * 40) + 60,
      nutrition: Math.floor(Math.random() * 40) + 60,
      stress: Math.floor(Math.random() * 40) + 60,
      hydration: Math.floor(Math.random() * 40) + 60,
    };
    
    const overallScore = Math.round(
      (breakdown.activity + breakdown.sleep + breakdown.nutrition + breakdown.stress + breakdown.hydration) / 5
    );
    
    data.push({
      date: date.toISOString().split('T')[0],
      overallScore,
      breakdown,
      trend: overallScore > 75 ? 'improving' : overallScore > 60 ? 'stable' : 'declining',
      streakDays: 7 - i,
      activityData: {
        date: date.toISOString().split('T')[0],
        steps: Math.floor(Math.random() * 8000) + 4000,
        distance: Math.floor(Math.random() * 6000) + 2000,
        caloriesBurned: Math.floor(Math.random() * 400) + 200,
        activeMinutes: Math.floor(Math.random() * 60) + 20,
        heartRateAvg: Math.floor(Math.random() * 20) + 65,
        source: 'manual',
      },
      sleepData: {
        date: date.toISOString().split('T')[0],
        totalSleepMinutes: Math.floor(Math.random() * 120) + 360,
        deepSleepMinutes: Math.floor(Math.random() * 60) + 60,
        sleepScore: Math.floor(Math.random() * 30) + 70,
        source: 'manual',
      },
    });
  }
  
  return data;
};

const mockAchievements: Achievement[] = [
  {
    id: '1',
    type: 'streak_7',
    title: '7-Day Warrior',
    description: 'Completed 7 days in a row!',
    icon: '🔥',
    earnedAt: new Date().toISOString(),
  },
  {
    id: '2',
    type: 'steps_10k',
    title: '10K Steps Club',
    description: 'Hit 10,000 steps in a day',
    icon: '👟',
    earnedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
  },
  {
    id: '3',
    type: 'hydration_hero',
    title: 'Hydration Hero',
    description: 'Met water goal for 5 days',
    icon: '💧',
    earnedAt: new Date(Date.now() - 86400000 * 5).toISOString(),
  },
];

const allAchievementDefs: Record<AchievementType, { title: string; description: string; icon: string }> = {
  streak_7: { title: '7-Day Warrior', description: 'Complete 7 days streak', icon: '🔥' },
  streak_30: { title: 'Monthly Master', description: 'Complete 30 days streak', icon: '⭐' },
  streak_100: { title: 'Century Champion', description: 'Complete 100 days streak', icon: '👑' },
  perfect_score: { title: 'Perfect Day', description: 'Achieve 100% health score', icon: '💯' },
  steps_10k: { title: '10K Steps Club', description: 'Hit 10,000 steps', icon: '👟' },
  sleep_champion: { title: 'Sleep Champion', description: '8+ hours sleep for a week', icon: '😴' },
  hydration_hero: { title: 'Hydration Hero', description: 'Meet water goal 5 days', icon: '💧' },
  early_bird: { title: 'Early Bird', description: 'Log before 8 AM for a week', icon: '🌅' },
  night_owl_recovery: { title: 'Night Owl Recovery', description: 'Improve sleep after bad week', icon: '🦉' },
  consistency_king: { title: 'Consistency King', description: 'Stable scores for 2 weeks', icon: '📊' },
  first_sync: { title: 'Connected', description: 'Sync your first device', icon: '📱' },
  multi_device: { title: 'Multi-Device', description: 'Connect 2+ devices', icon: '⌚' },
};

const HealthScorePage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const [weekData, setWeekData] = useState<DailyHealthScore[]>([]);
  const [todayScore, setTodayScore] = useState<DailyHealthScore | null>(null);
  const [streak, setStreak] = useState<StreakInfo>({
    currentStreak: 7,
    longestStreak: 14,
    lastCheckIn: new Date().toISOString(),
    streakStartDate: new Date(Date.now() - 86400000 * 6).toISOString(),
    totalCheckIns: 42,
    thisWeekCheckIns: 7,
    perfectDays: 3,
  });
  const [achievements] = useState<Achievement[]>(mockAchievements);
  const [showLogModal, setShowLogModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  
  // Manual input state
  const [manualInput, setManualInput] = useState({
    steps: 0,
    activeMinutes: 0,
    sleepHours: 7,
    sleepQuality: 7,
    calories: 0,
    protein: 0,
    water: 8,
    stressLevel: 5,
    mood: 7,
  });

  useEffect(() => {
    // Load mock data - replace with API call
    const data = generateMockWeekData();
    setWeekData(data);
    setTodayScore(data[data.length - 1]);
  }, []);

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#48bb78';
    if (score >= 60) return '#ecc94b';
    if (score >= 40) return '#ed8936';
    return '#f56565';
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Great';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    if (score >= 50) return 'Needs Work';
    return 'Poor';
  };

  const getTrendIcon = (trend: string): string => {
    if (trend === 'improving') return '📈';
    if (trend === 'stable') return '➡️';
    return '📉';
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (dateStr === today.toISOString().split('T')[0]) return 'Today';
    if (dateStr === yesterday.toISOString().split('T')[0]) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const handleLogSubmit = () => {
    // Calculate scores from manual input
    const activityScore = Math.min(100, Math.round((manualInput.steps / 10000) * 50 + (manualInput.activeMinutes / 60) * 50));
    const sleepScore = Math.min(100, Math.round((manualInput.sleepHours / 8) * 50 + (manualInput.sleepQuality / 10) * 50));
    const nutritionScore = Math.min(100, Math.round((manualInput.protein / 100) * 40 + Math.min(manualInput.calories, 2000) / 2000 * 60));
    const hydrationScore = Math.min(100, Math.round((manualInput.water / 8) * 100));
    const stressScore = Math.min(100, Math.round((10 - manualInput.stressLevel) * 10));
    
    const newBreakdown: HealthScoreBreakdown = {
      activity: activityScore,
      sleep: sleepScore,
      nutrition: nutritionScore,
      stress: stressScore,
      hydration: hydrationScore,
    };
    
    const overallScore = Math.round(
      (activityScore + sleepScore + nutritionScore + stressScore + hydrationScore) / 5
    );
    
    const today = new Date().toISOString().split('T')[0];
    const newScore: DailyHealthScore = {
      date: today,
      overallScore,
      breakdown: newBreakdown,
      trend: overallScore > 75 ? 'improving' : overallScore > 60 ? 'stable' : 'declining',
      streakDays: streak.currentStreak + 1,
      activityData: {
        date: today,
        steps: manualInput.steps,
        distance: Math.round(manualInput.steps * 0.762),
        caloriesBurned: Math.round(manualInput.steps * 0.04),
        activeMinutes: manualInput.activeMinutes,
        source: 'manual',
      },
      sleepData: {
        date: today,
        totalSleepMinutes: manualInput.sleepHours * 60,
        sleepScore: sleepScore,
        source: 'manual',
      },
    };
    
    setTodayScore(newScore);
    setStreak(prev => ({
      ...prev,
      currentStreak: prev.currentStreak + 1,
      lastCheckIn: new Date().toISOString(),
      totalCheckIns: prev.totalCheckIns + 1,
      thisWeekCheckIns: prev.thisWeekCheckIns + 1,
      perfectDays: overallScore >= 80 ? prev.perfectDays + 1 : prev.perfectDays,
    }));
    
    // Update week data
    const updatedWeekData = [...weekData];
    updatedWeekData[updatedWeekData.length - 1] = newScore;
    setWeekData(updatedWeekData);
    
    setShowLogModal(false);
  };

  // Circular progress component
  const CircularProgress: React.FC<{ score: number; size?: number; strokeWidth?: number }> = ({ 
    score, 
    size = 200, 
    strokeWidth = 12 
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (score / 100) * circumference;
    
    return (
      <div className="circular-progress" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            className="progress-bg"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
          />
          <circle
            className="progress-bar"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ stroke: getScoreColor(score) }}
          />
        </svg>
        <div className="progress-content">
          <span className="score-value">{score}</span>
          <span className="score-label">{getScoreLabel(score)}</span>
        </div>
      </div>
    );
  };

  // Mini score bar component
  const ScoreBar: React.FC<{ label: string; score: number; icon: string }> = ({ label, score, icon }) => (
    <div className="score-bar-item">
      <div className="score-bar-header">
        <span className="score-bar-icon">{icon}</span>
        <span className="score-bar-label">{label}</span>
        <span className="score-bar-value">{score}%</span>
      </div>
      <div className="score-bar-track">
        <div 
          className="score-bar-fill" 
          style={{ 
            width: `${score}%`,
            backgroundColor: getScoreColor(score)
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="health-score-page">
      <div className="health-score-container">
        {/* Header */}
        <div className="hs-header">
          <div className="hs-header-left">
            <h1>🏆 Daily Health Score</h1>
            <p className="hs-subtitle">Track your wellness journey</p>
          </div>
          <button className="log-today-btn" onClick={() => setShowLogModal(true)}>
            ✏️ Log Today
          </button>
        </div>

        {/* Main Score Card */}
        <div className="main-score-card">
          <div className="score-section">
            {todayScore && (
              <>
                <CircularProgress score={todayScore.overallScore} size={180} strokeWidth={14} />
                <div className="score-trend">
                  <span className="trend-icon">{getTrendIcon(todayScore.trend)}</span>
                  <span className="trend-text">
                    {todayScore.trend === 'improving' && 'You\'re doing great!'}
                    {todayScore.trend === 'stable' && 'Staying consistent'}
                    {todayScore.trend === 'declining' && 'Room for improvement'}
                  </span>
                </div>
              </>
            )}
          </div>
          
          <div className="breakdown-section">
            <h3>Score Breakdown</h3>
            {todayScore && (
              <div className="score-bars">
                <ScoreBar label="Activity" score={todayScore.breakdown.activity} icon="🏃" />
                <ScoreBar label="Sleep" score={todayScore.breakdown.sleep} icon="😴" />
                <ScoreBar label="Nutrition" score={todayScore.breakdown.nutrition} icon="🥗" />
                <ScoreBar label="Stress" score={todayScore.breakdown.stress} icon="🧘" />
                <ScoreBar label="Hydration" score={todayScore.breakdown.hydration} icon="💧" />
              </div>
            )}
          </div>
        </div>

        {/* Streak Card */}
        <div className="streak-card">
          <div className="streak-main">
            <div className="streak-fire">🔥</div>
            <div className="streak-info">
              <span className="streak-count">{streak.currentStreak}</span>
              <span className="streak-label">Day Streak</span>
            </div>
          </div>
          <div className="streak-stats">
            <div className="streak-stat">
              <span className="stat-value">{streak.longestStreak}</span>
              <span className="stat-label">Best Streak</span>
            </div>
            <div className="streak-stat">
              <span className="stat-value">{streak.totalCheckIns}</span>
              <span className="stat-label">Total Days</span>
            </div>
            <div className="streak-stat">
              <span className="stat-value">{streak.perfectDays}</span>
              <span className="stat-label">Perfect Days</span>
            </div>
          </div>
          <div className="streak-message">
            {streak.currentStreak >= 7 && '🎉 Amazing consistency! Keep it up!'}
            {streak.currentStreak >= 3 && streak.currentStreak < 7 && '💪 Great start! 7-day badge is close!'}
            {streak.currentStreak < 3 && '🌱 Every day counts. Build your streak!'}
          </div>
        </div>

        {/* Week Overview */}
        <div className="week-overview">
          <h3>📅 This Week</h3>
          <div className="week-chart">
            {weekData.map((day, index) => (
              <div 
                key={day.date} 
                className={`day-bar ${day.date === todayScore?.date ? 'today' : ''}`}
                onClick={() => setSelectedDay(day.date)}
              >
                <div className="day-bar-wrapper">
                  <div 
                    className="day-bar-fill"
                    style={{ 
                      height: `${day.overallScore}%`,
                      backgroundColor: getScoreColor(day.overallScore)
                    }}
                  />
                </div>
                <span className="day-score">{day.overallScore}</span>
                <span className="day-label">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(day.date).getDay()]}
                </span>
              </div>
            ))}
          </div>
          <div className="week-summary">
            <div className="week-stat">
              <span className="week-stat-label">Week Average</span>
              <span className="week-stat-value">
                {Math.round(weekData.reduce((sum, d) => sum + d.overallScore, 0) / weekData.length)}
              </span>
            </div>
            <div className="week-stat">
              <span className="week-stat-label">Best Day</span>
              <span className="week-stat-value">
                {Math.max(...weekData.map(d => d.overallScore))}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <h3>📊 Today's Stats</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-icon">👟</span>
              <span className="stat-number">{todayScore?.activityData?.steps?.toLocaleString() || '---'}</span>
              <span className="stat-name">Steps</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">🔥</span>
              <span className="stat-number">{todayScore?.activityData?.caloriesBurned || '---'}</span>
              <span className="stat-name">Calories</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">😴</span>
              <span className="stat-number">
                {todayScore?.sleepData ? Math.round(todayScore.sleepData.totalSleepMinutes / 60 * 10) / 10 : '---'}h
              </span>
              <span className="stat-name">Sleep</span>
            </div>
            <div className="stat-card">
              <span className="stat-icon">💓</span>
              <span className="stat-number">{todayScore?.activityData?.heartRateAvg || '---'}</span>
              <span className="stat-name">Avg HR</span>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="achievements-section">
          <h3>🏅 Achievements</h3>
          <div className="achievements-grid">
            {achievements.map((achievement) => (
              <div key={achievement.id} className="achievement-card earned">
                <span className="achievement-icon">{achievement.icon}</span>
                <span className="achievement-title">{achievement.title}</span>
                <span className="achievement-desc">{achievement.description}</span>
              </div>
            ))}
            {/* Locked achievements */}
            {Object.entries(allAchievementDefs)
              .filter(([type]) => !achievements.find(a => a.type === type))
              .slice(0, 3)
              .map(([type, def]) => (
                <div key={type} className="achievement-card locked">
                  <span className="achievement-icon">🔒</span>
                  <span className="achievement-title">{def.title}</span>
                  <span className="achievement-desc">{def.description}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Device Connection Placeholder */}
        <div className="device-section">
          <h3>⌚ Connect Devices</h3>
          <p className="device-info">
            Connect your fitness tracker for automatic data sync. Mobile app coming soon!
          </p>
          <div className="device-grid">
            <div className="device-card coming-soon">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Fitbit_logo.svg/200px-Fitbit_logo.svg.png" alt="Fitbit" className="device-logo" />
              <span className="device-name">Fitbit</span>
              <span className="device-status">Coming Soon</span>
            </div>
            <div className="device-card coming-soon">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Garmin_logo_2006.svg/200px-Garmin_logo_2006.svg.png" alt="Garmin" className="device-logo" />
              <span className="device-name">Garmin</span>
              <span className="device-status">Coming Soon</span>
            </div>
            <div className="device-card coming-soon">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5f/Google_Fit_icon_%282018%29.svg/200px-Google_Fit_icon_%282018%29.svg.png" alt="Google Fit" className="device-logo" />
              <span className="device-name">Google Fit</span>
              <span className="device-status">Coming Soon</span>
            </div>
            <div className="device-card coming-soon">
              <span className="device-logo-text">⌚</span>
              <span className="device-name">Apple Watch</span>
              <span className="device-status">Mobile App Required</span>
            </div>
          </div>
        </div>

        {/* AI Insight */}
        <div className="ai-insight-card">
          <div className="ai-icon">🤖</div>
          <div className="ai-content">
            <h4>AI Health Insight</h4>
            <p>
              {todayScore && todayScore.overallScore >= 80 
                ? "Fantastic day! Your activity and sleep are well balanced. Keep maintaining this routine for optimal health."
                : todayScore && todayScore.overallScore >= 60
                ? "Good progress! Consider adding 15 more minutes of activity and drinking one more glass of water to boost your score."
                : "Let's improve! Focus on getting 7-8 hours of sleep tonight and try a 20-minute walk. Small steps lead to big changes!"
              }
            </p>
          </div>
        </div>
      </div>

      {/* Log Modal */}
      {showLogModal && (
        <div className="log-modal-overlay" onClick={() => setShowLogModal(false)}>
          <div className="log-modal" onClick={e => e.stopPropagation()}>
            <div className="log-modal-header">
              <h2>📝 Log Today's Health</h2>
              <button className="close-modal" onClick={() => setShowLogModal(false)}>✕</button>
            </div>
            
            <div className="log-modal-content">
              {/* Activity */}
              <div className="log-section">
                <h3>🏃 Activity</h3>
                <div className="log-input-row">
                  <label>Steps</label>
                  <input
                    type="number"
                    value={manualInput.steps}
                    onChange={(e) => setManualInput(prev => ({ ...prev, steps: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
                <div className="log-input-row">
                  <label>Active Minutes</label>
                  <input
                    type="number"
                    value={manualInput.activeMinutes}
                    onChange={(e) => setManualInput(prev => ({ ...prev, activeMinutes: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Sleep */}
              <div className="log-section">
                <h3>😴 Sleep</h3>
                <div className="log-input-row">
                  <label>Hours Slept</label>
                  <input
                    type="number"
                    step="0.5"
                    value={manualInput.sleepHours}
                    onChange={(e) => setManualInput(prev => ({ ...prev, sleepHours: parseFloat(e.target.value) || 0 }))}
                    placeholder="7"
                  />
                </div>
                <div className="log-input-row">
                  <label>Sleep Quality (1-10)</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={manualInput.sleepQuality}
                    onChange={(e) => setManualInput(prev => ({ ...prev, sleepQuality: parseInt(e.target.value) }))}
                  />
                  <span className="range-value">{manualInput.sleepQuality}</span>
                </div>
              </div>

              {/* Nutrition */}
              <div className="log-section">
                <h3>🥗 Nutrition</h3>
                <div className="log-input-row">
                  <label>Calories Consumed</label>
                  <input
                    type="number"
                    value={manualInput.calories}
                    onChange={(e) => setManualInput(prev => ({ ...prev, calories: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
                <div className="log-input-row">
                  <label>Protein (g)</label>
                  <input
                    type="number"
                    value={manualInput.protein}
                    onChange={(e) => setManualInput(prev => ({ ...prev, protein: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
                <div className="log-input-row">
                  <label>Water (glasses)</label>
                  <input
                    type="number"
                    value={manualInput.water}
                    onChange={(e) => setManualInput(prev => ({ ...prev, water: parseInt(e.target.value) || 0 }))}
                    placeholder="8"
                  />
                </div>
              </div>

              {/* Mental */}
              <div className="log-section">
                <h3>🧘 Mental</h3>
                <div className="log-input-row">
                  <label>Stress Level (1-10)</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={manualInput.stressLevel}
                    onChange={(e) => setManualInput(prev => ({ ...prev, stressLevel: parseInt(e.target.value) }))}
                  />
                  <span className="range-value">{manualInput.stressLevel}</span>
                </div>
                <div className="log-input-row">
                  <label>Mood (1-10)</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={manualInput.mood}
                    onChange={(e) => setManualInput(prev => ({ ...prev, mood: parseInt(e.target.value) }))}
                  />
                  <span className="range-value">{manualInput.mood}</span>
                </div>
              </div>
            </div>

            <div className="log-modal-footer">
              <button className="cancel-btn" onClick={() => setShowLogModal(false)}>Cancel</button>
              <button className="save-btn" onClick={handleLogSubmit}>💾 Save & Calculate Score</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthScorePage;
